/**
 * Bulk action hook for CRM contacts and companies.
 *
 * `selectedIds` is local UI state (selection in a table); CRUD calls
 * use `useMutation` so React Query handles loading state and invalidates
 * the underlying list caches automatically.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api, { CrmBulkCompanyUpdates, CrmBulkContactUpdates } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function useBulkActions() {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => setSelectedIds(new Set(ids)), []);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const invalidateContacts = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.crm.contacts.all() });
  const invalidateCompanies = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.crm.companies.all() });

  const updateContactsMutation = useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: CrmBulkContactUpdates }) =>
      api.bulkUpdateContacts(ids, updates),
    onSuccess: (result) => {
      toast({ title: `Updated ${result.updated} contacts` });
      clearSelection();
      return invalidateContacts();
    },
    onError: () =>
      toast({ title: 'Error', description: 'Bulk update failed', variant: 'destructive' }),
  });

  const deleteContactsMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteContacts(ids),
    onSuccess: (result) => {
      toast({ title: `Deleted ${result.deleted} contacts` });
      clearSelection();
      return invalidateContacts();
    },
    onError: () =>
      toast({ title: 'Error', description: 'Bulk delete failed', variant: 'destructive' }),
  });

  const archiveContactsMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkArchiveContacts(ids),
    onSuccess: (result) => {
      toast({ title: `Archived ${result.updated} contacts` });
      clearSelection();
      return invalidateContacts();
    },
    onError: () =>
      toast({ title: 'Error', description: 'Bulk archive failed', variant: 'destructive' }),
  });

  const updateCompaniesMutation = useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: CrmBulkCompanyUpdates }) =>
      api.bulkUpdateCompanies(ids, updates),
    onSuccess: (result) => {
      toast({ title: `Updated ${result.updated} companies` });
      clearSelection();
      return invalidateCompanies();
    },
    onError: () =>
      toast({ title: 'Error', description: 'Bulk update failed', variant: 'destructive' }),
  });

  const deleteCompaniesMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteCompanies(ids),
    onSuccess: (result) => {
      toast({ title: `Deleted ${result.deleted} companies` });
      clearSelection();
      return invalidateCompanies();
    },
    onError: () =>
      toast({ title: 'Error', description: 'Bulk delete failed', variant: 'destructive' }),
  });

  const loading =
    updateContactsMutation.isPending ||
    deleteContactsMutation.isPending ||
    archiveContactsMutation.isPending ||
    updateCompaniesMutation.isPending ||
    deleteCompaniesMutation.isPending;

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    toggleSelect,
    selectAll,
    clearSelection,
    loading,
    bulkUpdateContacts: async (updates: CrmBulkContactUpdates) => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      try {
        return await updateContactsMutation.mutateAsync({ ids, updates });
      } catch {
        return undefined;
      }
    },
    bulkDeleteContacts: async () => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      try {
        return await deleteContactsMutation.mutateAsync(ids);
      } catch {
        return undefined;
      }
    },
    bulkArchiveContacts: async () => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      try {
        return await archiveContactsMutation.mutateAsync(ids);
      } catch {
        return undefined;
      }
    },
    bulkUpdateCompanies: async (updates: CrmBulkCompanyUpdates) => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      try {
        return await updateCompaniesMutation.mutateAsync({ ids, updates });
      } catch {
        return undefined;
      }
    },
    bulkDeleteCompanies: async () => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      try {
        return await deleteCompaniesMutation.mutateAsync(ids);
      } catch {
        return undefined;
      }
    },
  };
}
